<template>
  <UiModalShell
    :show="props.showModal"
    title="Manually add MCS customer"
    size="lg"
    @close="closeModal"
  >
    <!-- Loading Overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 bg-bg/70 backdrop-blur-sm flex items-center justify-center z-10 rounded-[var(--radius-lg)]"
    >
      <div class="flex flex-col items-center">
        <Loader2 :size="32" class="animate-spin text-lime" />
        <p class="mt-2 text-sm text-text-2">Adding customer&hellip;</p>
      </div>
    </div>

    <p class="text-[11px] text-text-3 mb-4">
      Use this only for dormant customers that haven&rsquo;t appeared in the catalog refresh.
    </p>

    <form @submit.prevent="submit" class="space-y-4">
      <!-- Error Message -->
      <div
        v-if="error"
        class="p-3 bg-[rgba(240,82,82,0.08)] border border-err/40 rounded-[var(--radius-md)]"
      >
        <p class="text-err text-sm">{{ error }}</p>
      </div>

      <!-- Site -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">
          Site <span class="text-err">*</span>
        </label>
        <UiSelect
          v-model.number="form.site_id"
          :disabled="sitesLoading || sitePreselected"
        >
          <option v-if="sitesLoading" :value="null">Loading sites&hellip;</option>
          <option v-else-if="!sites.length" :value="null">No active sites</option>
          <option v-else :value="null" disabled>Select a site</option>
          <option v-for="site in sites" :key="site.id" :value="site.id">
            {{ site.display_name }} ({{ site.name }})
          </option>
        </UiSelect>
        <p v-if="sitePreselected" class="text-[11px] text-text-3 mt-1">
          Site is locked for this context.
        </p>
      </div>

      <!-- Company ID -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">
          Company ID <span class="text-err">*</span>
        </label>
        <UiInput
          v-model.number="form.company_id"
          type="number"
          placeholder="e.g. 1"
        />
      </div>

      <!-- Customer No -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">
          Customer No <span class="text-err">*</span>
        </label>
        <UiInput
          v-model.number="form.customer_no"
          type="number"
          placeholder="e.g. 5321"
        />
      </div>

      <!-- Customer Name -->
      <div>
        <label class="block text-xs font-medium text-text-2 mb-1">
          Customer Name <span class="text-err">*</span>
        </label>
        <UiInput
          v-model="form.customer_name"
          placeholder="Acme Restaurant LLC"
        />
      </div>

      <!-- Hidden submit so Enter key works -->
      <button type="submit" class="hidden" aria-hidden="true" />
    </form>

    <template #footer>
      <UiButton variant="ghost" :disabled="loading" @click="closeModal">
        Cancel
      </UiButton>
      <UiButton
        variant="primary"
        :loading="loading"
        :disabled="!canSubmit"
        @click="submit"
      >
        Add customer
      </UiButton>
    </template>
  </UiModalShell>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { Loader2 } from 'lucide-vue-next';
import { AdminApiService, ApiError } from '@/services/adminApi';
import UiModalShell from '@/components/ui/UiModalShell.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiSelect from '@/components/ui/UiSelect.vue';
import UiButton from '@/components/ui/UiButton.vue';

const props = defineProps({
  showModal: {
    type: Boolean,
    default: false,
  },
  // If provided, the site dropdown is locked to this id.
  preselectedSiteId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close', 'added']);

const store = useStore();
const adminApi = new AdminApiService(store.state.apiUrl);

const loading = ref(false);
const sitesLoading = ref(false);
const error = ref(null);
const sites = ref([]);

const form = reactive({
  site_id: null,
  company_id: null,
  customer_no: null,
  customer_name: '',
});

const sitePreselected = computed(() => props.preselectedSiteId != null);

const canSubmit = computed(() => (
  form.site_id != null
  && form.company_id != null
  && form.customer_no != null
  && !!form.customer_name.trim()
));

async function fetchSites() {
  sitesLoading.value = true;
  try {
    const data = await adminApi.listSites();
    sites.value = Array.isArray(data) ? data.filter((s) => s.is_active) : [];
  } catch (err) {
    console.error('Failed to load sites:', err);
    sites.value = [];
  } finally {
    sitesLoading.value = false;
  }
}

onMounted(fetchSites);

// Reset form on open and apply preselected site
watch(
  () => props.showModal,
  async (isOpen) => {
    if (!isOpen) return;
    error.value = null;
    form.site_id = props.preselectedSiteId ?? null;
    form.company_id = null;
    form.customer_no = null;
    form.customer_name = '';
    if (!sites.value.length) await fetchSites();
  }
);

watch(
  () => props.preselectedSiteId,
  (v) => {
    if (v != null) form.site_id = v;
  }
);

const closeModal = () => {
  if (loading.value) return;
  error.value = null;
  emit('close');
};

async function submit() {
  error.value = null;
  if (!canSubmit.value) {
    error.value = 'Please fill in all fields.';
    return;
  }
  loading.value = true;
  try {
    const created = await adminApi.addManualMcsCustomer({
      site_id: form.site_id,
      company_id: form.company_id,
      customer_no: form.customer_no,
      customer_name: form.customer_name.trim(),
    });
    emit('added', created);
  } catch (err) {
    console.error('Manual add customer failed:', err);
    if (err instanceof ApiError && err.status === 409) {
      error.value = err.message || 'Customer already exists.';
    } else {
      error.value = err.message || 'Failed to add customer.';
    }
  } finally {
    loading.value = false;
  }
}

defineExpose({ fetchSites });
</script>
